import { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "../../features/auth";
import { ScheduleConflictItem } from "@myapp/contract";
import PageLayout from "../../shared/ui/layouts/PageLayout/PageLayout";
import { PATHS } from "../../app/routes/paths";
import { useAbsenceDetail, useUpdateAbsence, useDeleteAbsence, getAbsenceCategoryLabel } from "../../entities/absence";
import Loader from "../../shared/ui/feedback/Loader/Loader";
import {
  TimeManagementFormWidget,
  toUpdateDto,
  fromTimeManagementDetail,
  type TimeManagementFormValues,
} from "../../features/time-management";
import { useSuccessDialog } from "../../shared/hooks/useSuccessDialog";
import { SuccessDialog } from "../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { ConflictDialog } from "../../shared/ui/feedback/ConflictDialog/ConflictDialog";
import { BlockerDialog } from "../../shared/ui/feedback/BlockerDialog/BlockerDialog";
import { parseConflictsFromError } from "../../shared/lib/errors/parseConflictsFromError";
import { parseCompletedBlockersFromError } from "../../shared/lib/errors/parseCompletedBlockersFromError";
import { useDeleteDialog } from "../../shared/hooks/useDeleteDialog";
import DeleteItemDialog from "../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { ReadOnlyNotice } from "../../shared/ui/feedback/ReadOnlyNotice/ReadOnlyNotice";

export function WorkerZeitmanagementEditPage() {
  const navigate = useNavigate();
  const { absenceId } = useParams<{ absenceId: string }>();
  const id = Number(absenceId);

  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useAbsenceDetail(id);
  const updateMutation = useUpdateAbsence();
  const deleteMutation = useDeleteAbsence();
  const submitRef = useRef<(() => void) | null>(null);
  const { showSuccess, dialogProps } = useSuccessDialog();
  const deleteDialog = useDeleteDialog();

  const [conflicts, setConflicts] = useState<ScheduleConflictItem[] | null>(null);
  const [blockers, setBlockers] = useState<ScheduleConflictItem[] | null>(null);
  const pendingValuesRef = useRef<TimeManagementFormValues | null>(null);

  const userId = Number(user?.id ?? 0);
  const isAssigned = data ? data.technicians.some((t) => t.id === userId) : false;
  const canView = isAssigned;
  const canEdit = isAssigned && data?.category === "PERSONAL_LEAVE";

  useEffect(() => {
    if (!isLoading && data && !canView) {
      navigate(PATHS.workerCalendar, { replace: true });
    }
  }, [isLoading, data, canView, navigate]);

  if (isLoading || !data || !canView) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  const handleSubmit = async (values: TimeManagementFormValues, force = false) => {
    try {
      const dto = toUpdateDto(values, force);
      await updateMutation.mutateAsync({ id, dto });
      showSuccess("Erfolgreich gespeichert", () => navigate(PATHS.workerCalendar));
    } catch (err) {
      const blocking = parseCompletedBlockersFromError(err);
      if (blocking) {
        setBlockers(blocking);
        return;
      }
      const detected = parseConflictsFromError(err);
      if (detected) {
        pendingValuesRef.current = values;
        setConflicts(detected);
      } else {
        throw err;
      }
    }
  };

  const handleConflictConfirm = async () => {
    if (!pendingValuesRef.current) return;
    await handleSubmit(pendingValuesRef.current, true);
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  const handleConflictCancel = () => {
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  return (
    <>
      <PageLayout
        pageTitle={`${getAbsenceCategoryLabel(data.category)} ${canEdit ? "bearbeiten" : "ansehen"}`}
        scrollable
        back
        backTo={PATHS.workerCalendar}
        primaryAction={canEdit ? {
          label: "Speichern",
          onClick: () => submitRef.current?.(),
          loading: updateMutation.isPending,
        } : undefined}
        removeAction={canEdit ? {
          onClick: () =>
            deleteDialog.openDelete("Eintrag wirklich löschen?", async () => {
              await deleteMutation.mutateAsync(id);
              navigate(PATHS.workerCalendar);
            }),
        } : undefined}
      >
        {!canEdit && <ReadOnlyNotice />}
        <TimeManagementFormWidget
          category={data.category}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
          submitRef={submitRef}
          initialValues={fromTimeManagementDetail(data)}
          hideTechnicianSelect
          readOnly={!canEdit}
        />
      </PageLayout>
      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => { if (!open) dialogProps.onClose(); }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
      <ConflictDialog
        open={conflicts !== null}
        conflicts={conflicts ?? []}
        onConfirm={handleConflictConfirm}
        onCancel={handleConflictCancel}
        isLoading={updateMutation.isPending}
      />
      <BlockerDialog
        open={blockers !== null}
        blockers={blockers ?? []}
        onClose={() => setBlockers(null)}
        onGoToReschedule={() => navigate(PATHS.reschedule)}
      />
      <DeleteItemDialog deleteModal={deleteDialog} />
    </>
  );
}
